import React from "react";
import { CgWorkAlt } from "react-icons/cg";
import { FaReact, FaDocker, FaAws } from "react-icons/fa";
import {
  SiExpo,
  SiFirebase,
  SiFramer,
  SiGit,
  SiGithub,
  SiHtml5,
  SiJavascript,
  SiMongodb,
  SiNextdotjs,
  SiPython,
  SiReact,
  SiTailwindcss,
  SiTypescript,
  SiUnrealengine,
  SiPytorch,
  SiTensorflow,
  SiPostgresql,
  SiKeras,
  SiCplusplus,
  SiDocker,
  SiKubernetes,
  SiOnnx,
  SiNvidia,
  SiOpencv,
} from "react-icons/si";
import { LuGraduationCap, LuBriefcase, LuBuilding2 } from "react-icons/lu";
import pidImg from "@/public/polyu-sm.png";
import caperImg from "@/public/instacart-logo.png";
import tiktokImg from "@/public/tiktok-clean.png";
import aifiImg from "@/public/aifi-clean.png";
import polyuImg from "@/public/polyu-sm.png";

const en = {
  links: [
    {
      name: "Home",
      hash: "#home",
    },
    {
      name: "About",
      hash: "#about",
    },
    {
      name: "Projects",
      hash: "#projects",
    },
    {
      name: "Skills",
      hash: "#skills",
    },
    {
      name: "Experience",
      hash: "#experience",
    },
    {
      name: "LLMVisualizer",
      hash: "/llmpage",
    },
  ],
  intro: {
    greeting: "Hi, I'm Wangpeng An.",
    heading: "Full Stack Staff Machine Learning Engineer",
    main: "I build multimodal LLM and VLM systems, from training through deployment. I've spent about ten years on this, and most of my time goes into making inference cheap and reliable enough to run at scale, not into the model itself. I've worked on everything from CVPR research to systems that process hundreds of millions of videos a day.",
    occupation:
      "Currently at Instacart (Caper Cart) | Previously at TikTok & Meta AI Research (FAIR) | CVPR Spotlight Author",
    contactButton: "Contact me here",
    downloadCV: "Download CV",
    statusChip: "Building VLM systems at Instacart",
  },
  about: {
    title: "About Me",
    first:
      "I started out in control engineering rather than ML, and it still shapes how I think. My CVPR Spotlight paper came out of that background: if you treat training a deep network as a feedback-control problem, you can use a PID controller to drive the optimizer (later extended to IEEE TNNLS, IF 11.368). I did my Master's at Tsinghua working on deep learning optimization, pose estimation, and face recognition, with papers at CVPR, IEEE TNNLS, and Pattern Recognition, and a U.S. patent for automated-checkout tracking.",
    second:
      "Working at Instacart, TikTok, Meta FAIR, and AiFi taught me that most of what makes production ML succeed or fail happens around the model, not in it. At TikTok, replacing roughly 800 narrow perception models with a single multimodal LLM only worked once INT8 quantization and tensor-parallel serving on Inf2 had cut inference cost in half. At AiFi, we used cameras only, no shelf sensors, which kept the hardware cheap but put all the pressure on detection and tracking. I'm comfortable across the whole stack: research, large-scale training and inference, deployment on Docker, Kubernetes, TensorRT and edge, and the serving and product layers on top. I also review for CVPR, ECCV, ICCV, and IEEE TPAMI."
  },
  projects: {
    title: "Research & Projects",
    data: [
      {
        title: "PID Controller for Deep Learning",
        description:
            "SGD with momentum is essentially a PI controller: it uses the present gradient and the accumulated past, but no derivative term. Adding the D term lets the optimizer anticipate overshoot, which made training faster and more stable. CVPR 2018 Spotlight, extended to IEEE TNNLS (IF 11.368).",
        tags: ["CVPR Spotlight", "Optimization", "PyTorch"],
        imageUrl: pidImg,
        imageFit: "contain" as const,
        imageBg: "oklch(23% 0.025 250)",
        repositoryUrl: "https://openaccess.thecvf.com/content_cvpr_2018/html/An_A_PID_Controller_CVPR_2018_paper.html",
        repoType: "CVPR Page",
        demoType: "Paper",
        demoUrl: "https://arxiv.org/abs/1808.03337",
      },
      {
        title: "Caper Cart Shopping Assistant",
        description:
          "At Instacart: a VLM on the Caper Cart that confirms each item as a shopper scans it. It reaches 95% top-1 accuracy across more than 300K SKUs, and the serving and evaluation infra I built brought mis-identifications down by 30%. The cart runs on fixed latency, power, and cost budgets, so a lot of the work is deciding what runs on-device and what runs off it. I own this end-to-end, from the model to the product experience.",
        tags: ["VLM", "Multimodal", "Edge Inference", "Full Stack"],
        imageUrl: caperImg,
        imageFit: "contain" as const,
        imageBg: "oklch(97% 0.03 145)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Industry",
        demoUrl: "#",
      },
      {
        title: "Multimodal LLM at Scale",
        description:
          "At TikTok: a single multimodal LLM that turns video into dense text and replaces about 800 narrow perception models. Replacing that many models is only worth it if the one is cheaper to run than all of them combined, so most of the effort went into INT8 quantization and tensor-parallel serving on Inf2. That cut inference cost in half and made it affordable at 300 million videos a day. Later expanded to recommendation, ads, and search (AWS re:Invent 2024).",
        tags: ["LLM", "Multimodal", "Inf2", "re:Invent 2024"],
        imageUrl: tiktokImg,
        imageFit: "contain" as const,
        imageBg: "oklch(12% 0.005 0)",
        repositoryUrl: "https://www.youtube.com/watch?v=cdrZHimJY2k&t=2186",
        repoType: "re:Invent Talk",
        demoType: "AWS Blog",
        demoUrl: "https://aws.amazon.com/cn/blogs/machine-learning/bytedance-processes-billions-of-daily-videos-using-their-multimodal-video-understanding-models-on-aws-inferentia2/",
      },
      {
        title: "Autonomous Checkout System",
        description:
          "At AiFi: camera-only autonomous checkout, similar to Amazon Go but without the shelf weight sensors. Using cameras only keeps the store cheap to build, but it means the vision system has to do everything: person and product detection and tracking that holds up through occlusion and crowds, accurately enough to charge the right cart. I led those algorithms. U.S. Patent US11393213B2.",
        tags: ["Object Detection", "Tracking", "Patent"],
        imageUrl: aifiImg,
        imageFit: "contain" as const,
        imageBg: "oklch(94% 0.04 210)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Patent",
        demoUrl: "https://patents.google.com/patent/US11393213B2",
      },
      {
        title: "PolyU-Face System",
        description:
          "At HK PolyU: a full face pipeline covering detection, alignment, and recognition. Building the whole chain made something clear that papers tend to gloss over: alignment errors early on limit recognition accuracy later much more than the recognition model does. Also contributed to multi-person 2D pose estimation (ICME 2018 oral).",
        tags: ["Face Recognition", "Pose Estimation", "Research"],
        imageUrl: polyuImg,
        imageFit: "contain" as const,
        imageBg: "oklch(18% 0.025 27)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Research",
        demoUrl: "#",
      },
    ],
  },
  skills: {
    title: "My Skills",
    data: [
      // Programming Languages
      {
        skill: "Python",
        icon: React.createElement(SiPython),
        color: "#3572A5",
      },
      {
        skill: "C++",
        icon: React.createElement(SiCplusplus),
        color: "#00599C",
      },
      // ML Frameworks
      {
        skill: "PyTorch",
        icon: React.createElement(SiPytorch),
        color: "#EE4C2C",
      },
      {
        skill: "TensorFlow",
        icon: React.createElement(SiTensorflow),
        color: "#FF6F00",
      },
      {
        skill: "Keras",
        icon: React.createElement(SiKeras),
        color: "#D00000",
      },
      {
        skill: "ONNX",
        icon: React.createElement(SiOnnx),
        color: "#005CED",
      },
      {
        skill: "TensorRT",
        icon: React.createElement(SiNvidia),
        color: "#76B900",
      },
      {
        skill: "OpenCV",
        icon: React.createElement(SiOpencv),
        color: "#5C3EE8",
      },
      // Deployment
      {
        skill: "Docker",
        icon: React.createElement(SiDocker),
        color: "#2496ED",
      },
      {
        skill: "Kubernetes",
        icon: React.createElement(SiKubernetes),
        color: "#326CE5",
      },
      // Web Development
      {
        skill: "TypeScript",
        icon: React.createElement(SiTypescript),
        color: "#3178c6",
      },
      {
        skill: "React",
        icon: React.createElement(SiReact),
        color: "#61dafb",
      },
      {
        skill: "Next.js",
        icon: React.createElement(SiNextdotjs),
        color: "#000000",
      },
      {
        skill: "Tailwind",
        icon: React.createElement(SiTailwindcss),
        color: "#38bdf8",
      },
      // Tools
      {
        skill: "Git",
        icon: React.createElement(SiGit),
        color: "#f05032",
      },
      {
        skill: "PostgreSQL",
        icon: React.createElement(SiPostgresql),
        color: "#336791",
      },
    ],
  },
  experience: {
    title: "My Experience",
    data: [
      {
        title: "Instacart Inc. - Full Stack Staff ML Engineer",
        location: "California, USA",
        description:
          "Built a Vision Language Model on the Caper Cart that confirms items as shoppers scan them: 95% top-1 accuracy across 300K+ SKUs, with a 30% drop in mis-identifications. I own the system end-to-end, across model, inference, and the on-cart product experience.",
        icon: React.createElement(LuBriefcase),
        date: "April 2025 - Present",
      },
      {
        title: "TikTok Inc. - Senior ML Engineer",
        location: "San Jose, California, USA",
        description:
          "Trust & Safety and Content Ads: Built a full-stack multimodal LLM system that converts videos into dense text and detects unsafe content, processing 300 million videos daily on Inf2 and outperforming 800 traditional perception models. Expanded the multimodal LLM to recommendation, ads, and search (presented at AWS re:Invent 2024), and lifted audio moderation metrics from 5% to 10% by integrating Whisper.",
        icon: React.createElement(LuBriefcase),
        date: "September 2021 - April 2025",
      },
      {
        title: "Meta (Facebook) - CV Engineer at FAIR",
        location: "Menlo Park, California, USA",
        description:
          "Facebook AI Research: Worked on Explainable AI to open the 'black box' of computer vision models, and Responsible AI for inclusive & fairness in Computer Vision systems.",
        icon: React.createElement(LuBriefcase),
        date: "December 2020 - August 2021",
      },
      {
        title: "AiFi Inc. - Senior Research Engineer",
        location: "Santa Clara, California, USA",
        description:
          "Led computer vision detection algorithms for RGB-only autonomous checkout solution (similar to Amazon Go). Developed customer detection + tracking and product detection + tracking systems. U.S. Patent holder.",
        icon: React.createElement(LuBriefcase),
        date: "June 2018 - November 2020",
      },
      {
        title: "HK Polytechnic University - Research Assistant",
        location: "Hong Kong",
        description:
          "Developed PID optimizer to accelerate CNN training (CVPR 2018 Spotlight). Built the PolyU-Face system for face detection, alignment, and recognition.",
        icon: React.createElement(LuBuilding2),
        date: "October 2016 - April 2018",
      },
      {
        title: "Tsinghua University - Master's Degree",
        location: "Beijing, China",
        description:
          "Control Engineering with research focus on deep learning optimization, human pose estimation, object detection, and face recognition. Student instructor for Big Data class.",
        icon: React.createElement(LuGraduationCap),
        date: "August 2015 - July 2018",
      },
      {
        title: "Taiyuan Iron and Steel Corp. - Control Technician",
        location: "Taiyuan, China",
        description:
          "Focused on tuning PID controller parameters in steel milling machines. Applied control theory to industrial automation.",
        icon: React.createElement(LuBriefcase),
        date: "July 2012 - September 2014",
      },
      {
        title: "Kunming University of Science and Technology - Bachelor's Degree",
        location: "Kunming, China",
        description:
          "Instrument and Measurement Science with concentration in control theory. Foundation in PID control systems and signal processing.",
        icon: React.createElement(LuGraduationCap),
        date: "September 2008 - July 2012",
      },
    ],
  },
  contact: {
    title: "Contact Me",
    message:
      "Feel free to reach out, especially about new roles. You can use the form below, email me directly, or find me on LinkedIn or GitHub. I'll get back to you.",
    placeholderEmail: "Your email",
    placeholderMessage: "Your message",
    buttonSubmit: "Submit",
    buttonCopy: "Copy Email Address",
    toastSent: "Email sent successfully!",
    toastCopied: "Email address copied to clipboard!",
  },
  footer: {
    text: "All rights reserved.",
    subtitle: "About this website",
    about:
      "built with React, Next.js, TypeScript, Tailwind CSS, Framer Motion, Vercel hosting.",
  },
};

export default en;
